import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { finalize } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as mainActions from 'src/app/state/app.actions';
import { ErrorMessage } from 'src/app/common/messages';
import { mainState } from 'src/app/state/app.reducer';
import { usersService } from 'src/app/services/services/users.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { Router } from '@angular/router';
import { AddUsergroupDialogComponent, AddUsergroupDialogData } from './dialogs/add-usergroup-dialog.component';
import { AddTemplateProfileDialogComponent, AddTemplateProfileDialogData } from './dialogs/add-template-profile-dialog.component';
import { trigger, transition, style, animate } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  UsersUsergroupsModel,
  UserGroupLong,
  UsergroupProfile,
  SaveUsergroupChanges,
  SaveUsergroupChangesResponse,
  CheckForActiveAgentsResponse,
  TemplateProfile,
  UsergroupQuality
} from '../models';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';

@Component({
  selector: 'app-usergroups',
  templateUrl: './usergroups.component.html',
  styleUrls: ['./usergroups.component.scss'],
  animations: [
    trigger('slideInFromRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('0.3s ease-out', style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        animate('0.3s ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ],
  standalone: false
})
export class UsergroupsComponent implements OnInit, OnDestroy {
  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label: 'User Groups', url: '/users/usergroups' }
  ];

  // Data
  model: UsersUsergroupsModel | null = null;
  userGroups: UserGroupLong[] = [];
  expandedGroups: Set<number> = new Set();
  loadingGroups: Set<number> = new Set();
  groupProfiles: { [key: number]: UsergroupProfile[] } = {};

  // State
  isLoading = false;
  filterText = '';

  // Changes tracking
  nameChanges: { [key: number]: string } = {};
  billingChanges: { [key: number]: boolean } = {};
  profileChanges: { [key: number]: boolean } = {};
  originalNames: { [key: number]: string } = {};
  originalBilling: { [key: number]: boolean } = {};

  // Selected profiles for copy
  selectedProfiles: { [key: number]: boolean } = {};

  // Skills panel state
  showSkillsPanel = false;
  currentProfileForSkills: UsergroupProfile | null = null;
  currentGroupIdForSkills: number | null = null;
  private originalBodyOverflow: string = '';

  // Sortable mode state
  sortableGroups: Set<number> = new Set();

  constructor(
    private usersService: usersService,
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private router: Router,
    private store: Store<mainState>
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    // Restore body scroll if panel is still open
    if (this.showSkillsPanel) {
      this.enableBodyScroll();
    }
  }

  loadData(): void {
    this.isLoading = true;
    this.usersService.getUsersUsergroups()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (model: UsersUsergroupsModel) => {
          this.model = model;
          this.userGroups = model.allUserGroupsList || [];
          this.userGroups.forEach(ug => {
            this.originalNames[ug.id] = ug.name;
            this.originalBilling[ug.id] = ug.isBillable || false;
          });
          this.model.groupTypes =
          {
            1: this.i18n.transform('users:usergroups_type_agent') as string,
            2: this.i18n.transform('users:usergroups_type_admin') as string,
            3: this.i18n.transform('users:usergroups_type_wallboard') as string,
            4: this.i18n.transform('users:usergroups_type_bot_only') as string
          };
        },
        error: () => {
          // Error handling
        }
      });
  }

  toggleGroup(groupId: number): void {
    if (this.expandedGroups.has(groupId)) {
      this.expandedGroups.delete(groupId);
    } else {
      this.expandedGroups.add(groupId);
      if (!this.groupProfiles[groupId]) {
        this.loadGroupProfiles(groupId);
      }
    }
  }

  loadGroupProfiles(groupId: number): void {
    this.loadingGroups.add(groupId);
    this.usersService.getUserGroupProfiles(groupId)
      .pipe(finalize(() => this.loadingGroups.delete(groupId)))
      .subscribe({
        next: (profiles: UsergroupProfile[]) => {
          this.groupProfiles[groupId] = profiles;
        },
        error: () => {
          // Error handling
        }
      });
  }

  onNameChange(groupId: number, newName: string): void {
    this.nameChanges[groupId] = newName;
    this.updateButtonsState(groupId);
  }

  onBillingChange(groupId: number, isBillable: boolean): void {
    this.billingChanges[groupId] = isBillable;
    this.updateButtonsState(groupId);
  }

  updateButtonsState(groupId: number): void {
    const hasChanges =
      (this.nameChanges[groupId] && this.nameChanges[groupId] !== this.originalNames[groupId]) ||
      (this.billingChanges[groupId] !== undefined && this.billingChanges[groupId] !== this.originalBilling[groupId]) ||
      this.profileChanges[groupId];
    // Update button states in template
  }

  hasChanges(groupId: number): boolean {
    return (
      (this.nameChanges[groupId] && this.nameChanges[groupId] !== this.originalNames[groupId]) ||
      (this.billingChanges[groupId] !== undefined && this.billingChanges[groupId] !== this.originalBilling[groupId]) ||
      this.profileChanges[groupId]
    );
  }

  saveChanges(groupId: number): void {
    const group = this.userGroups.find(ug => ug.id === groupId);
    if (!group) return;

    // Validate that all profiles have at least one skill
    if (this.hasProfileWithoutSkills(groupId)) {
      return;
    }

    // Validate that all profiles have a name
    if (this.hasProfileWithEmptyName(groupId)) {
      return;
    }

    // Collect profile changes
    const currentProfiles = this.groupProfiles[groupId] || [];
    const addedProfiles: UsergroupProfile[] = [];
    const editedProfiles: UsergroupProfile[] = [];
    const deletedProfiles: number[] = [];

    // Track which profiles were originally loaded (have IDs)
    const originalProfileIds = new Set<number>();

    // Reload original profiles to compare
    this.usersService.getUserGroupProfiles(groupId)
      .subscribe({
        next: (originalProfiles: UsergroupProfile[]) => {
          originalProfiles.forEach(p => {
            if (p.id) {
              originalProfileIds.add(p.id);
            }
          });

          // Determine added, edited, and deleted profiles
          currentProfiles.forEach(profile => {
            if (!profile.id) {
              // New profile (no ID)
              addedProfiles.push(profile);
            } else if (originalProfileIds.has(profile.id)) {
              // Existing profile that might have been edited
              const original = originalProfiles.find(p => p.id === profile.id);
              if (original && this.hasProfileChanged(original, profile)) {
                editedProfiles.push(profile);
              }
            }
          });

          // Find deleted profiles (in original but not in current)
          originalProfiles.forEach(original => {
            if (original.id && !currentProfiles.find(p => p.id === original.id)) {
              deletedProfiles.push(original.id);
            }
          });

          const payload: SaveUsergroupChanges = {
            usergroupId: groupId,
            newUsergroupName: this.nameChanges[groupId] || group.name,
            isBillable: this.originalBilling[groupId],
            newIsBillable: this.billingChanges[groupId] !== undefined ? this.billingChanges[groupId] : this.originalBilling[groupId],
            addedProfiles: addedProfiles,
            editedProfiles: editedProfiles,
            deletedProfiles: deletedProfiles
          };

          // Check if any edited profiles are template-based with skills changes
          const hasTemplateProfileWithSkillsChanges = this.hasTemplateProfileWithSkillsChanges(
            editedProfiles,
            originalProfiles
          );

          // If template-based profiles with skills changes exist, show confirmation first
          if (hasTemplateProfileWithSkillsChanges) {
            this.showTemplateProfileWarningWithActiveAgentsCheck(groupId, payload);
          } else {
            // No template profile warning needed, proceed with active agents check
            this.checkActiveAgentsAndSave(groupId, payload);
          }
        },
        error: () => {
          // If we can't load original profiles, proceed with current state
          const payload: SaveUsergroupChanges = {
            usergroupId: groupId,
            newUsergroupName: this.nameChanges[groupId] || group.name,
            isBillable: this.originalBilling[groupId],
            newIsBillable: this.billingChanges[groupId] !== undefined ? this.billingChanges[groupId] : this.originalBilling[groupId],
            addedProfiles: currentProfiles.filter(p => !p.id),
            editedProfiles: [],
            deletedProfiles: []
          };

          // Note: In error case, we can't check original profiles, so we skip template check
          // Proceed directly with active agents check
          this.checkActiveAgentsAndSave(groupId, payload);
        }
      });
  }

  private hasProfileChanged(original: UsergroupProfile, current: UsergroupProfile): boolean {
    if (original.name !== current.name) return true;
    if (original.number !== current.number) return true;
    if (original.enableForChildren !== current.enableForChildren) return true;

    // Compare skills
    const originalSkillsStr = JSON.stringify(original.skills);
    const currentSkillsStr = JSON.stringify(current.skills);
    return originalSkillsStr !== currentSkillsStr;
  }

  private hasSkillsChanged(original: UsergroupProfile, current: UsergroupProfile): boolean {
    // Compare skills
    const originalSkillsStr = JSON.stringify(original.skills);
    const currentSkillsStr = JSON.stringify(current.skills);
    return originalSkillsStr !== currentSkillsStr;
  }

  private hasTemplateProfileWithSkillsChanges(
    editedProfiles: UsergroupProfile[],
    originalProfiles: UsergroupProfile[]
  ): boolean {
    return editedProfiles.some(editedProfile => {
      // Check if profile is based on a template
      if (!editedProfile.templateProfileId || !editedProfile.id) {
        return false;
      }

      // Find the original profile
      const original = originalProfiles.find(p => p.id === editedProfile.id);
      if (!original) {
        return false;
      }

      // Check if skills have changed
      return this.hasSkillsChanged(original, editedProfile);
    });
  }

  private showTemplateProfileWarningWithActiveAgentsCheck(
    groupId: number,
    payload: SaveUsergroupChanges
  ): void {
    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      width: '600px',

      data: {
        confirmText: this.i18n.transform('Save') as string,
        cancelText: this.i18n.transform('Cancel') as string,
        showTitleInHeader: true,
        additionalText: this.i18n.transform('users:usergroup_help_updatetmpprofilewarning_body') as string,
        title: this.i18n.transform('users:usergroup_help_updatetmpprofilewarning_header') as string,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // User confirmed, proceed with active agents check and save
        this.checkActiveAgentsAndSave(groupId, payload);
      }
    });
  }

  private checkActiveAgentsAndSave(groupId: number, payload: SaveUsergroupChanges): void {
    // Check for active agents first
    this.usersService.checkForActiveAgents(payload)
      .subscribe({
        next: (response: CheckForActiveAgentsResponse) => {
          if (response.haveSkillsBeenChanged && response.numberOfActiveAgents > 0) {
            // Show warning dialog for active agents
            this.showActiveAgentsWarning(groupId, payload, response.numberOfActiveAgents);
          } else {
            this.doSaveChanges(groupId, payload, false);
          }
        },
        error: () => {
          // Error handling - proceed with save anyway
          this.doSaveChanges(groupId, payload, false);
        }
      });
  }

  doSaveChanges(groupId: number, payload: SaveUsergroupChanges, hasActiveAgents: boolean): void {
    this.usersService.saveUsergroupChanges(payload)
      .subscribe({
        next: (response: SaveUsergroupChangesResponse) => {
          if (response.validateUsergroupName === false) {
            // Show validation error
            return;
          }

          // Update local state
          if (payload.newUsergroupName) {
            const group = this.userGroups.find(ug => ug.id === groupId);
            if (group) {
              group.name = payload.newUsergroupName;
              this.originalNames[groupId] = payload.newUsergroupName;
            }
          }

          if (payload.newIsBillable !== undefined) {
            this.originalBilling[groupId] = payload.newIsBillable;
          }

          delete this.nameChanges[groupId];
          delete this.billingChanges[groupId];
          this.profileChanges[groupId] = false;
          this.sortableGroups.delete(groupId);

          // Reload profiles
          this.loadGroupProfiles(groupId);
        },
        error: () => {
          this.store.dispatch(
            mainActions.addMessage({
              message: new ErrorMessage(
                this.i18n.transform('users:shared_err_postError'),
                this.i18n.transform('users:saveUsergroupChangesError')
              )
            })
          );
        }
      });
  }

  undoChanges(groupId: number): void {
    this.usersService.getUserGroupNamefromId(groupId)
      .subscribe({
        next: (group: UserGroupLong) => {
          this.originalNames[groupId] = group.name;
          this.originalBilling[groupId] = group.isBillable || false;
          delete this.nameChanges[groupId];
          delete this.billingChanges[groupId];
          this.profileChanges[groupId] = false;
          this.sortableGroups.delete(groupId);
          this.loadGroupProfiles(groupId);
        },
        error: () => {
          // Error handling
        }
      });
  }

  deleteGroup(groupId: number): void {
    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      width: '600px',

      data: {
        confirmText: this.i18n.transform('Delete') as string,
        cancelText: this.i18n.transform('Cancel') as string,
        showTitleInHeader: true,
        additionalText: this.i18n.transform('users:shared_deleteConfirmDescription') as string,
        title: this.i18n.transform('users:shared_deleteConfirm') as string,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.checkIfGroupIsEmpty(groupId);
      }
    });
  }

  checkIfGroupIsEmpty(groupId: number): void {
    this.usersService.getUsergroupUserList(groupId)
      .subscribe({
        next: (users: any[]) => {
          if (users && users.length > 0) {
            // Show error: group is not empty
            this.dialog.open(DialogHelpComponent, {
              panelClass: 'dialog-help-pannel',
              backdropClass: 'backdrop-invisible',
              width: '600px',
              data: {
                title: this.i18n.transform('users:usergroups_deleteUsergroup'),
                text: this.i18n.transform('users:usergroups_err_deletionfailed'),
                renderAsHtml: true
              }
            });
          } else {
            this.doDeleteGroup(groupId);
          }
        },
        error: () => {
          // Error handling
        }
      });
  }

  doDeleteGroup(groupId: number): void {
    this.usersService.deleteUsergroup(groupId)
      .subscribe({
        next: () => {
          this.userGroups = this.userGroups.filter(ug => ug.id !== groupId);
          this.expandedGroups.delete(groupId);
          delete this.groupProfiles[groupId];
        },
        error: () => {
          // Error handling
        }
      });
  }

  openAddGroupDialog(): void {
    if (!this.model) {
      return;
    }

    const dialogRef = this.dialog.open(AddUsergroupDialogComponent, {
      width: '433px',
      disableClose: true,
      data: {
        model: this.model
      } as AddUsergroupDialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.reload) {
        // Reload the page data
        this.loadData();
      }
    });
  }

  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      height: '70vh',
      data: {
        title: this.i18n.transform('users:help_title_usergroup', { defaultValue: 'Help for User Groups' }),
        text: this.i18n.transform('users:help_usergroup', { defaultValue: '' }),
        renderAsHtml: true
      }
    });
  }

  showActiveAgentsWarning(groupId: number, payload: SaveUsergroupChanges, agentCount: number): void {
    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      width: '500px',

      data: {
        confirmText: this.i18n.transform('Save') as string,
        cancelText: this.i18n.transform('Cancel') as string,

        title: `There are ${agentCount} active agents. Do you want to continue?`,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.doSaveChanges(groupId, payload, true);
      }
    });
  }

  getGroupName(groupId: number): string {
    return this.nameChanges[groupId] || this.originalNames[groupId] || '';
  }

  getGroupBilling(groupId: number): boolean {
    return this.billingChanges[groupId] !== undefined
      ? this.billingChanges[groupId]
      : (this.originalBilling[groupId] || false);
  }

  get filteredGroups(): UserGroupLong[] {
    if (!this.filterText) {
      return this.userGroups;
    }
    const filter = this.filterText.toLowerCase();
    return this.userGroups.filter(ug =>
      ug.name.toLowerCase().includes(filter)
    );
  }

  addProfile(groupId: number): void {
    if (!this.model || !this.model.canEditUsers) {
      return;
    }

    const profiles = this.groupProfiles[groupId] || [];
    const nextNumber = profiles.length > 0
      ? Math.max(...profiles.map(p => p.number)) + 1
      : 1;

    const defaultName = `Profile ${nextNumber}`;
    const profileName = "";
    const newProfile: UsergroupProfile = {
      name: profileName.trim(),
      number: nextNumber,
      enableForChildren: 0,
      skills: []
    };

    if (!this.groupProfiles[groupId]) {
      this.groupProfiles[groupId] = [];
    }
    this.groupProfiles[groupId].push(newProfile);
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }

  onProfileUseChange(groupId: number, profile: UsergroupProfile, isEnabled: boolean): void {
    profile.enableForChildren = isEnabled ? 1 : 0;
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }

  CopyProfile(groupId, profile): void {
    if (!this.model || !this.model.canEditUsers) {
      return;
    }
    const profiles = this.groupProfiles[groupId] || [];
    const maxNumber = profiles.length > 0
      ? Math.max(...profiles.map(p => p.number))
      : 0;
    const newNumber = maxNumber + 1;
    const newName = `${profile.name} (Copy)`;
    const copiedProfile: UsergroupProfile = {
      name: newName,
      number: newNumber,
      enableForChildren: profile.enableForChildren,
      skills: profile.skills.map(service => ({
        serviceId: service.serviceId,
        qualities: service.qualities.map(quality => ({
          ...quality
        }))
      }))
    };

    if (!this.groupProfiles[groupId]) {
      this.groupProfiles[groupId] = [];
    }
    this.groupProfiles[groupId].push(copiedProfile);
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }




  copySelectedProfiles(groupId: number): void {
    if (!this.model || !this.model.canEditUsers) {
      return;
    }

    const selectedProfileIds = Object.keys(this.selectedProfiles)
      .map(id => parseInt(id, 10))
      .filter(id => this.selectedProfiles[id]);

    if (selectedProfileIds.length === 0) {
      return;
    }

    // Get all profiles from all groups
    const allProfiles = Object.values(this.groupProfiles).flat();
    const profilesToCopy = allProfiles.filter(p => p.id && selectedProfileIds.includes(p.id));

    if (profilesToCopy.length === 0) {
      return;
    }

    const profiles = this.groupProfiles[groupId] || [];
    const maxNumber = profiles.length > 0
      ? Math.max(...profiles.map(p => p.number))
      : 0;

    profilesToCopy.forEach((profile, index) => {
      const newNumber = maxNumber + index + 1;
      const newName = `${profile.name} (Copy)`;

      const copiedProfile: UsergroupProfile = {
        name: newName,
        number: newNumber,
        enableForChildren: profile.enableForChildren,
        skills: profile.skills.map(service => ({
          serviceId: service.serviceId,
          qualities: service.qualities.map(quality => ({
            ...quality
          }))
        }))
      };

      if (!this.groupProfiles[groupId]) {
        this.groupProfiles[groupId] = [];
      }
      this.groupProfiles[groupId].push(copiedProfile);
    });

    // Clear selections
    selectedProfileIds.forEach(id => {
      this.selectedProfiles[id] = false;
    });

    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }

  markForCopy(groupId: number, profile: UsergroupProfile): void {
    this.selectedProfiles[profile.id] = !this.selectedProfiles[profile.id];
  }

  addTemplateProfile(groupId: number): void {
    if (!this.model || !this.model.canEditUsers || !this.model.templateProfiles || this.model.templateProfiles.length === 0) {
      return;
    }

    const dialogRef = this.dialog.open(AddTemplateProfileDialogComponent, {
      width: '500px',
      data: {
        templateProfiles: this.model.templateProfiles
      } as AddTemplateProfileDialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.selectedTemplates && result.selectedTemplates.length > 0) {
        const profiles = this.groupProfiles[groupId] || [];
        const nextNumber = profiles.length > 0
          ? Math.max(...profiles.map(p => p.number)) + 1
          : 1;

        if (!this.groupProfiles[groupId]) {
          this.groupProfiles[groupId] = [];
        }

        // Convert each selected template profile to usergroup profile
        result.selectedTemplates.forEach((template: TemplateProfile, index: number) => {
          const newProfile: UsergroupProfile = {
            name: template.name,
            number: nextNumber + index,
            enableForChildren: template.enableForChildren,
            templateProfileId: template.id,
            templateProfileName: template.name,
            skills: template.skills.map(templateService => ({
              serviceId: templateService.serviceId,
              qualities: templateService.qualities.map(templateQuality => ({
                qualityId: templateQuality.qualityId,
                categoryNumber: templateQuality.categoryNumber,
                name: templateQuality.name,
                userLevel: templateQuality.userLevel,
                isFiltered: false,
                isCategoryFiltered: false
              }))
            }))
          };
          this.groupProfiles[groupId].push(newProfile);
        });

        this.profileChanges[groupId] = true;
        this.updateButtonsState(groupId);
      }
    });
  }

  openAddSkillsDialog(groupId: number, profile: UsergroupProfile): void {
    if (!this.model || !this.model.canEditUsers || !this.model.usergroupServiceQualities) {
      return;
    }

    this.currentProfileForSkills = { ...profile }; // Create a copy
    this.currentGroupIdForSkills = groupId;
    this.showSkillsPanel = true;
    this.disableBodyScroll();
  }

  closeSkillsPanel(): void {
    this.showSkillsPanel = false;
    this.currentProfileForSkills = null;
    this.currentGroupIdForSkills = null;
    this.enableBodyScroll();
  }

  private disableBodyScroll(): void {
    // Save the original overflow value
    this.originalBodyOverflow = document.body.style.overflow || '';
    // Disable scrolling on the body
    document.body.style.overflow = 'hidden';
  }

  private enableBodyScroll(): void {
    // Restore the original overflow value
    document.body.style.overflow = this.originalBodyOverflow;
  }

  onSkillsSaved(updatedProfile: UsergroupProfile): void {
    if (!this.currentGroupIdForSkills || !this.currentProfileForSkills) {
      return;
    }

    const groupId = this.currentGroupIdForSkills;
    const profiles = this.groupProfiles[groupId] || [];

    // Find and update the profile
    // For profiles with ID, match by ID
    // For new profiles without ID, match by number (which is unique within a group)
    const index = profiles.findIndex(p => {
      if (p.id && this.currentProfileForSkills!.id) {
        return p.id === this.currentProfileForSkills!.id;
      }
      // For profiles without ID, match by number
      if (!p.id && !this.currentProfileForSkills!.id) {
        return p.number === this.currentProfileForSkills!.number;
      }
      return false;
    });

    if (index !== -1) {
      profiles[index] = updatedProfile;
      this.profileChanges[groupId] = true;
      this.updateButtonsState(groupId);
    }

    this.closeSkillsPanel();
  }

  hasSelectedProfiles(): boolean {
    return Object.values(this.selectedProfiles).some(selected => selected);
  }

  deleteProfile(groupId: number, profile: UsergroupProfile): void {
    if (!this.model || !this.model.canEditUsers) {
      return;
    }

    if (!profile.id) {
      // Profile is not saved yet, just remove from local array
      const profiles = this.groupProfiles[groupId] || [];
      const index = profiles.findIndex(p => p === profile);
      if (index !== -1) {
        profiles.splice(index, 1);
        this.profileChanges[groupId] = true;
        this.updateButtonsState(groupId);
      }
      return;
    }

    // Mark profile for deletion
    const profiles = this.groupProfiles[groupId] || [];
    const index = profiles.findIndex(p => p.id === profile.id);
    if (index !== -1) {
      // Remove from local array - it will be sent in deletedProfiles when saving
      profiles.splice(index, 1);

      // Clear selection if this profile was selected
      if (this.selectedProfiles[profile.id]) {
        this.selectedProfiles[profile.id] = false;
      }

      this.profileChanges[groupId] = true;
      this.updateButtonsState(groupId);
    }
  }

  onQualityLevelChange(groupId: number, profile: UsergroupProfile, qualityId: number, newLevel: string): void {
    // Mark profile as changed
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);

    // Update the quality level in the profile
    if (profile.skills) {
      profile.skills.forEach(service => {
        const quality = service.qualities.find(q => q.qualityId === qualityId);
        if (quality) {
          quality.userLevel = parseInt(newLevel, 10);
        }
      });
    }
  }

  removeQuality(groupId: number, profile: UsergroupProfile, qualityId: number): void {
    // Mark profile as changed
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);

    // Remove the quality from the profile
    if (profile.skills) {
      profile.skills.forEach(service => {
        service.qualities = service.qualities.filter(q => q.qualityId !== qualityId);
      });
      // Remove services that have no qualities
      profile.skills = profile.skills.filter(service => service.qualities.length > 0);
    }
  }

  hasSkills(profile: UsergroupProfile): boolean {
    if (!profile.skills || profile.skills.length === 0) {
      return false;
    }
    return profile.skills.some(service => service.qualities && service.qualities.length > 0);
  }

  hasProfileWithoutSkills(groupId: number): boolean {
    const profiles = this.groupProfiles[groupId] || [];
    return profiles.some(profile => !this.hasSkills(profile));
  }

  hasProfileWithEmptyName(groupId: number): boolean {
    const profiles = this.groupProfiles[groupId] || [];
    return profiles.some(profile => !profile.name || profile.name.trim() === '');
  }

  getAllQualities(profile: UsergroupProfile): UsergroupQuality[] {
    const allQualities: UsergroupQuality[] = [];
    if (profile.skills) {
      profile.skills.forEach(service => {
        if (service.qualities) {
          allQualities.push(...service.qualities);
        }
      });
    }
    return allQualities;
  }

  editProfilesNumbers(groupId: number): void {
    if (!this.model || !this.model.canEditUsers) {
      return;
    }
    // Toggle sortable mode for this group
    if (this.sortableGroups.has(groupId)) {
      this.sortableGroups.delete(groupId);
    } else {
      this.sortableGroups.add(groupId);
    }
  }

  isSortable(groupId: number): boolean {
    return this.sortableGroups.has(groupId);
  }

  onProfileDrop(event: CdkDragDrop<UsergroupProfile[]>, groupId: number): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const profiles = this.groupProfiles[groupId] || [];
    moveItemInArray(profiles, event.previousIndex, event.currentIndex);

    // Update profile numbers based on new order
    profiles.forEach((profile, index) => {
      profile.number = index + 1;
    });

    // Mark profile changes
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }

  onProfileNameChange(groupId: number, profile: UsergroupProfile, newName: string): void {
    profile.name = newName;
    this.profileChanges[groupId] = true;
    this.updateButtonsState(groupId);
  }

  goToUsers(groupId: number): void {
    window.open("/admin/UsersUsers/index/" + groupId.toString());
  }
}

